import {localStoragePersistSafely} from "@/shared/lib/localStorage";
import {toast} from "vue-sonner";

const CONFIG = {
    BASE_RELOAD_INTERVAL: 3000,
    RELOAD_LIMIT: 5,
    TOAST_DURATION: 5000,
    MODAL_RETRY_DELAY: 2 * 60 * 1000
};

function getReloadState() {
    let countStr = sessionStorage.getItem('reload-count');
    let lastReloadStr = sessionStorage.getItem('last-reload');
    let backoffStr = sessionStorage.getItem('backoff-factor');

    if (countStr === null) countStr = localStorage.getItem('reload-count') || '0';
    if (lastReloadStr === null) lastReloadStr = localStorage.getItem('last-reload') || '0';
    if (backoffStr === null) backoffStr = localStorage.getItem('backoff-factor') || '1';

    return {
        count: parseInt(countStr, 10),
        lastReload: parseInt(lastReloadStr, 10),
        backoffFactor: parseInt(backoffStr, 10)
    };
}

function safePersist(key, value) {
    sessionStorage.setItem(key, value);
    localStoragePersistSafely(key, value);
}

function setReloadState({count, lastReload, backoffFactor}) {
    safePersist('reload-count', count.toString());
    safePersist('last-reload', lastReload.toString());
    safePersist('backoff-factor', backoffFactor.toString());
}

function shouldReload(errorMessage) {
    const {count, lastReload, backoffFactor} = getReloadState();
    if (count >= CONFIG.RELOAD_LIMIT) {
        console.warn('Reload limit reached.');
        return false;
    }

    const currentInterval = CONFIG.BASE_RELOAD_INTERVAL * backoffFactor;
    if (Date.now() - lastReload < currentInterval) {
        console.warn(`Reload throttled. Wait ${currentInterval}ms.`);
        return false;
    }

    setReloadState({
        count: count + 1,
        lastReload: Date.now(),
        backoffFactor: backoffFactor * 2
    });
    console.warn('Initiating reload due to:', errorMessage);
    return true;
}

let toastId;

function reloadPage(errorMessage) {
    if (shouldReload(errorMessage)) {
        if (toastId) toast.dismiss(toastId);
        toastId = toast.info('New version available. Reloading...', {
            duration: CONFIG.TOAST_DURATION,
            onAutoClose: () => {
                console.info('Auto-reloading.');
                window.location.reload();
            }
        });

        const {count} = getReloadState();
        if (count > 2) showUpdateModal();
    }
}

function showUpdateModal() {
    let modal = document.getElementById('version-update-modal');
    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'version-update-modal';
        modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background-color:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:9999;';

        const modalContent = document.createElement('div');
        modalContent.style.cssText = 'background-color:white;padding:20px;border-radius:5px;max-width:500px;text-align:center;';
        modalContent.innerHTML = `
            <h2 style="margin-top:0;">Application Update Required</h2>
            <p>A new version is available. Reload to apply updates.</p>
            <div style="margin-top:20px;">
                <button id="update-now-btn" style="padding:8px 16px;background-color:#4CAF50;color:white;border:none;border-radius:4px;cursor:pointer;margin-right:10px;">Update Now</button>
                <button id="update-later-btn" style="padding:8px 16px;background-color:#f44336;color:white;border:none;border-radius:4px;cursor:pointer;">Later</button>
            </div>
        `;
        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        document.getElementById('update-now-btn').addEventListener('click', () => window.location.reload());
        document.getElementById('update-later-btn').addEventListener('click', () => {
            modal.style.display = 'none';
            setTimeout(() => modal.style.display = 'flex', CONFIG.MODAL_RETRY_DELAY);
        });
    } else {
        modal.style.display = 'flex';
    }
}

async function checkVersionAndReload(release, currentEnvironment) {
    try {
        if (currentEnvironment === "development") {
            window.appVersion = ":DEV";
            return;
        }

        const response = await fetch(`version.json?_=${Date.now()}`);
        if (!response.ok) {
            console.warn(`Failed to fetch version.json: ${response.status}`);
            window.appVersion = release.split('@')[1] || release;
            return;
        }

        const data = await response.json();
        const serverVersion = data.version.split('@')[1] || data.version;
        window.appVersion = serverVersion;
        console.log(`Server: ${serverVersion}, Current: ${release}, Env: ${data.environment}`);
        localStoragePersistSafely('app-version', serverVersion);

        if (release !== data.version) {
            console.info(`Version changed: ${release} -> ${serverVersion}. Reloading.`);
            window.location.reload();
        }
    } catch (error) {
        console.warn('Version check error:', error);
    }
}

export async function registerGlobalErrorListener(release, environment) {
    try {
        await checkVersionAndReload(release, environment);
        if (environment === "development") return;

        console.log(`Version tracking initialized: ${release}, ${environment}`);
        const isResourceError = (msg) => /Failed to fetch|NetworkError|Loading chunk/i.test(msg);
        const checkError = (message) => {
            if (message && isResourceError(message)) {
                console.info('Resource failure detected. Reloading...');
                reloadPage(message);
            }
        };

        const handleRejection = (event) => {
            const message = event.reason?.message || 'Unknown error';
            if (!message) return;
            console.warn('Unhandled rejection:', message);
            checkError(message);
        };

        const handleError = (event) => {
            const {message} = event;
            if (!message) return;
            console.warn('Global error:', message);
            checkError(message);
        };

        window.addEventListener('unhandledrejection', handleRejection);
        window.addEventListener('error', handleError);
        window.addEventListener('rejectionhandled', handleRejection);

        window.addEventListener('beforeunload', () => {
            window.removeEventListener('unhandledrejection', handleRejection);
            window.removeEventListener('rejectionhandled', handleRejection);
            window.removeEventListener('error', handleError);
            if (toastId) toast.dismiss(toastId);
        });
    } catch (error) {
        console.error("Failed to register global error listener:", error);
    }
}